// pages/driver/DeliveryTracking/DeliveryTracking.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Toast } from "primereact/toast";
import axios from "axios";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../firebase/firebase";

// Component imports
import DeliveryMap from "../deliveryTrackingComponents/DeliveryMap/DeliveryMap";
import TopNavigationBar from "../deliveryTrackingComponents/TopNavigationBar/TopNavigationBar";
import LocationBanner from "../deliveryTrackingComponents/LocationBanner/LocationBanner";
import BottomDrawer from "../deliveryTrackingComponents/BottomDrawer/BottomDrawer";
import StopMenu from "../deliveryTrackingComponents/StopMenu/StopMenu";
import StopNavigationDialog from "../deliveryTrackingComponents/StopNavigationDialog/StopNavigationDialog";
import PauseDialog from "../deliveryTrackingComponents/PauseDialog/PauseDialog";
import DirectionsModal from "../deliveryTrackingComponents/DirectionsModal/DirectionsModal";

// Services
import locationTrackingService from "../../../services/locationTrackingService";
import offlineDeliveryService from "../../../services/offlineDeliveryService";

import styles from "./DeliveryTracking.module.scss";
import { DEV } from "../../../services/constants";
import useViewOnly from "../../../hooks/useViewOnly";

const DeliveryTracking = () => {
    const { id } = useParams();
    const toast = useRef(null);
    const history = useHistory();
    const viewOnly = useViewOnly();

    // API and data states
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deliveryData, setDeliveryData] = useState(null);
    const [deliveryStops, setDeliveryStops] = useState([]);
    const [deliveryRoute, setDeliveryRoute] = useState(null);
    const [deliveryStatistics, setDeliveryStatistics] = useState(null);
    const [currentStop, setCurrentStop] = useState(null);

    // Location states
    const [driverLocation, setDriverLocation] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const [watchId, setWatchId] = useState(null);

    // UI states
    const [elapsedTime, setElapsedTime] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
    const [navigationActive, setNavigationActive] = useState(false);
    const [showStopDialog, setShowStopDialog] = useState(false);
    const [selectedStop, setSelectedStop] = useState(null);
    const [showDirections, setShowDirections] = useState(false);

    // Pause states
    const [showPauseDialog, setShowPauseDialog] = useState(false);
    const [activePause, setActivePause] = useState(null);
    const [pauseElapsedTime, setPauseElapsedTime] = useState(0);

    // Menu states
    const [showStopMenu, setShowStopMenu] = useState(false);
    const [selectedStopForMenu, setSelectedStopForMenu] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

    // Route calculation control
    const [shouldCalculateRoute, setShouldCalculateRoute] = useState(false);

    // Route optimization states
    const [optimizationInProgress, setOptimizationInProgress] = useState(false);
    const [lastOptimizedTime, setLastOptimizedTime] = useState(null);
    const autoOptimizedRef = useRef(false);

    // Offline sync states
    const [offlineCount, setOfflineCount] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);

    // Initialize location tracking service
    useEffect(() => {
        const initTracking = async () => {
            try {
                await locationTrackingService.init();
                console.log("✅ Location tracking service initialized");

                await locationTrackingService.resumeTrackingIfNeeded();
            } catch (error) {
                console.error("❌ Error initializing location tracking:", error);
            }
        };

        initTracking();

        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.addEventListener("message", (event) => {
                if (event.data.type === "GET_TOKEN") {
                    event.ports[0].postMessage({
                        token: localStorage.getItem("token"),
                    });
                }
            });
        }

        return () => {
            console.log("DeliveryTracking component unmounting - tracking continues");
        };
    }, []);

    // Stop tracking when delivery is completed or incomplete
    useEffect(() => {
        if (deliveryData?.status === "Completed" || deliveryData?.status === "Incomplete") {
            console.log("Delivery finished, stopping location tracking");
            locationTrackingService.stopTracking();
        }
    }, [deliveryData?.status]);

    // Auto-start location tracking when delivery is In Transit
    useEffect(() => {
        const autoStartTracking = async () => {
            if (!deliveryData || loading) {
                console.log("⏳ Waiting for delivery data...");
                return;
            }

            const isInTransit = deliveryData.status === "In Transit";
            const userId = localStorage.getItem("userId");
            const driverId = localStorage.getItem("userId");
            const deliveryId = deliveryData._id;

            console.log("═══════════════════════════════════════");
            console.log("🔍 AUTO-START TRACKING CHECK");
            console.log("═══════════════════════════════════════");
            console.log("Delivery Status:", deliveryData.status);
            console.log("Is In Transit:", isInTransit);
            console.log("Delivery ID:", deliveryId);
            console.log("Driver ID:", driverId);
            console.log("User ID:", userId);
            console.log("IDs Match:", userId === driverId);

            const isTrackingLocation = localStorage.getItem("isTrackingLocation");
            const activeDeliveryId = localStorage.getItem("activeDeliveryId");

            console.log("Current State:");
            console.log("  isTrackingLocation:", isTrackingLocation);
            console.log("  activeDeliveryId:", activeDeliveryId);
            console.log("═══════════════════════════════════════");

            if (isInTransit && userId === driverId && deliveryId) {
                const isAlreadyTracking = isTrackingLocation === "true";
                const isSameDelivery = activeDeliveryId === deliveryId;

                if (!isAlreadyTracking) {
                    console.log("🚀 NO ACTIVE TRACKING - STARTING IMMEDIATELY...");

                    try {
                        await locationTrackingService.startTracking(deliveryId, driverId);

                        console.log("✅ Location tracking started successfully");

                        if (toast.current) {
                            toast.current.show({
                                severity: "success",
                                summary: "Location Tracking Started",
                                detail: "Your location is being tracked",
                                life: 3000,
                            });
                        }
                    } catch (error) {
                        console.error("❌ Error starting location tracking:", error);

                        if (toast.current) {
                            toast.current.show({
                                severity: "error",
                                summary: "Tracking Error",
                                detail: error.message || "Failed to start location tracking",
                                life: 3000,
                            });
                        }
                    }
                } else if (!isSameDelivery) {
                    console.log("⚠️ SWITCHING TO NEW DELIVERY...");

                    try {
                        await locationTrackingService.stopTracking();
                        await locationTrackingService.startTracking(deliveryId, driverId);

                        console.log("✅ Switched to new delivery");

                        if (toast.current) {
                            toast.current.show({
                                severity: "info",
                                summary: "Tracking Switched",
                                detail: "Now tracking current delivery",
                                life: 3000,
                            });
                        }
                    } catch (error) {
                        console.error("❌ Error switching tracking:", error);
                    }
                } else {
                    console.log("✅ Already tracking this delivery");
                }
            } else {
                if (!isInTransit) {
                    console.log("⚠️ Delivery is not In Transit - status:", deliveryData.status);
                }
                if (userId !== driverId) {
                    console.log("⚠️ User is not the assigned driver");
                }
            }
        };

        if (deliveryData && !loading) {
            console.log("🚀 Delivery data ready, checking tracking...");
            autoStartTracking();
        }
    }, [deliveryData, loading]);

    // Monitor online status and auto-sync offline deliveries
    useEffect(() => {
        const handleOnline = () => {
            console.log("🌐 Back online - checking for offline deliveries...");
            const count = offlineDeliveryService.getOfflineCount();

            if (count > 0) {
                if (toast.current) {
                    toast.current.show({
                        severity: "info",
                        summary: "Back Online",
                        detail: `Syncing ${count} offline delivery(s)...`,
                        life: 3000,
                    });
                }

                setTimeout(() => {
                    syncOfflineDeliveries();
                }, 2000);
            }
        };

        const handleOffline = () => {
            console.log("📴 Gone offline");
            if (toast.current) {
                toast.current.show({
                    severity: "warn",
                    summary: "Offline Mode",
                    detail: "Deliveries will be saved locally",
                    life: 3000,
                });
            }
        };

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        // Check offline count on mount
        setOfflineCount(offlineDeliveryService.getOfflineCount());

        // If online and have offline deliveries, sync
        if (navigator.onLine && offlineDeliveryService.getOfflineCount() > 0) {
            setTimeout(() => {
                syncOfflineDeliveries();
            }, 3000);
        }

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);

    // Sync offline deliveries
    const syncOfflineDeliveries = async () => {
        const offlineDeliveries = offlineDeliveryService.getOfflineDeliveries();
        const pendingDeliveries = offlineDeliveries.filter((d) => d.status === "pending");

        if (pendingDeliveries.length === 0) {
            console.log("✅ No offline deliveries to sync");
            return;
        }

        console.log(`🔄 Syncing ${pendingDeliveries.length} offline deliveries...`);
        setIsSyncing(true);

        for (const delivery of pendingDeliveries) {
            try {
                console.log(`📤 Syncing delivery: ${delivery.id}`);

                // Check if still online
                if (!navigator.onLine) {
                    console.log("📴 Went offline during sync - stopping");
                    break;
                }

                // Upload photo to Firebase
                let photoUrl = null;
                if (delivery.photoDataUrl) {
                    const photoBlob = offlineDeliveryService.base64ToBlob(delivery.photoDataUrl);
                    const photoFileName = `deliveries/${delivery.deliveryId}/stops/${delivery.stopId}/photo_${Date.now()}.jpg`;
                    const photoStorageRef = ref(storage, photoFileName);
                    await uploadBytes(photoStorageRef, photoBlob);
                    photoUrl = await getDownloadURL(photoStorageRef);
                    console.log("✅ Photo uploaded");
                }

                // Upload signature to Firebase
                let signatureUrl = null;
                if (delivery.signatureDataUrl) {
                    const signatureBlob = offlineDeliveryService.base64ToBlob(delivery.signatureDataUrl);
                    const signatureFileName = `deliveries/${delivery.deliveryId}/stops/${delivery.stopId}/signature_${Date.now()}.png`;
                    const signatureStorageRef = ref(storage, signatureFileName);
                    await uploadBytes(signatureStorageRef, signatureBlob);
                    signatureUrl = await getDownloadURL(signatureStorageRef);
                    console.log("✅ Signature uploaded");
                }

                // Submit to API
                if (delivery.type === "complete") {
                    const response = await axios.post(
                        `${DEV}/delivery/${delivery.deliveryId}/stop/${delivery.stopId}/proof`,
                        {
                            photoUrl,
                            signatureUrl,
                            scannedPackageIds: delivery.scannedPackageIds,
                            timestamp: delivery.timestamp,
                        },
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${localStorage.getItem("token")}`,
                            },
                        },
                    );

                    if (response.data.success) {
                        console.log(`✅ Delivery ${delivery.id} synced successfully`);
                        offlineDeliveryService.removeOfflineDelivery(delivery.id);

                        if (toast.current) {
                            toast.current.show({
                                severity: "success",
                                summary: "Synced",
                                detail: `Stop #${delivery.stopNumber} - ${delivery.clientName}`,
                                life: 3000,
                            });
                        }
                    }
                } else if (delivery.type === "incomplete") {
                    const response = await axios.post(
                        `${DEV}/delivery/${delivery.deliveryId}/stop/${delivery.stopId}/mark-incomplete`,
                        {
                            reason: delivery.reason,
                            photoUrl,
                            signatureUrl,
                            scannedPackageIds: delivery.scannedPackageIds,
                            timestamp: delivery.timestamp,
                        },
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${localStorage.getItem("token")}`,
                            },
                        },
                    );

                    if (response.data.success) {
                        console.log(`✅ Incomplete ${delivery.id} synced successfully`);
                        offlineDeliveryService.removeOfflineDelivery(delivery.id);

                        if (toast.current) {
                            toast.current.show({
                                severity: "info",
                                summary: "Synced",
                                detail: `Stop #${delivery.stopNumber} incomplete status`,
                                life: 3000,
                            });
                        }
                    }
                }
            } catch (error) {
                console.error(`❌ Error syncing delivery ${delivery.id}:`, error);
                offlineDeliveryService.updateDeliveryStatus(delivery.id, "error", error.message);

                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Sync Failed",
                        detail: `Failed to sync Stop #${delivery.stopNumber}`,
                        life: 3000,
                    });
                }
            }
        }

        // Refresh delivery data after sync
        await fetchDeliveryDetails();

        setIsSyncing(false);
        setOfflineCount(offlineDeliveryService.getOfflineCount());

        console.log("✅ Offline sync complete");
    };

    // Reorder delivery stops by road distance from driver's location
    const reorderDeliveryStops = useCallback(
        async (location) => {
            const loc = location || driverLocation;
            if (!loc) {
                if (toast.current) {
                    toast.current.show({
                        severity: "warn",
                        summary: "Location Unavailable",
                        detail: "Driver location is required to optimize the route",
                        life: 4000,
                    });
                }
                return;
            }

            setOptimizationInProgress(true);
            try {
                const response = await axios.post(
                    `${DEV}/deliveries/${id}/reorder-stops`,
                    {
                        currentLocation: {
                            latitude: loc.lat,
                            longitude: loc.lng,
                        },
                    },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    },
                );

                if (response.data.success) {
                    const { stops } = response.data.data;
                    setDeliveryStops(stops);
                    setLastOptimizedTime(new Date());

                    if (toast.current) {
                        toast.current.show({
                            severity: "success",
                            summary: "Route Optimized",
                            detail: "Stops reordered by road distance",
                            life: 3000,
                        });
                    }
                }
            } catch (error) {
                console.error("❌ Error reordering stops:", error);
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Optimization Failed",
                        detail: error.response?.data?.message || "Failed to optimize route. Current order retained.",
                        life: 4000,
                    });
                }
            } finally {
                setOptimizationInProgress(false);
            }
        },
        [id, driverLocation],
    ); // eslint-disable-line react-hooks/exhaustive-deps

    // Enhanced location tracking
    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            setLocationError("Geolocation is not supported by this browser");
            return;
        }

        const options = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: navigationActive ? 5000 : 60000,
        };

        const successCallback = (position) => {
            const newLocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy,
                timestamp: new Date().toISOString(),
            };

            setDriverLocation(newLocation);
            setLocationError(null);
        };

        const errorCallback = (error) => {
            let errorMessage = "Unable to get location";
            switch (error.code) {
                case error.PERMISSION_DENIED:
                    errorMessage = "Location access denied by user";
                    break;
                case error.POSITION_UNAVAILABLE:
                    errorMessage = "Location information unavailable";
                    break;
                case error.TIMEOUT:
                    errorMessage = "Location request timeout";
                    break;
                default:
                    errorMessage = "Unknown location error";
                    break;
            }

            setLocationError(errorMessage);
            const fallbackLocation = deliveryData?.origin ? { lat: deliveryData.origin.latitude, lng: deliveryData.origin.longitude } : { lat: 33.006257, lng: 73.15074 };

            setDriverLocation(fallbackLocation);

            if (toast.current) {
                toast.current.show({
                    severity: "warn",
                    summary: "Location Warning",
                    detail: `${errorMessage}. Using fallback location.`,
                    life: 5000,
                });
            }
        };

        navigator.geolocation.getCurrentPosition(successCallback, errorCallback, options);

        if (navigationActive) {
            if (watchId) {
                navigator.geolocation.clearWatch(watchId);
            }

            const id = navigator.geolocation.watchPosition(successCallback, errorCallback, {
                enableHighAccuracy: true,
                timeout: 8000,
                maximumAge: 5000,
            });

            const locationInterval = setInterval(() => {
                navigator.geolocation.getCurrentPosition(successCallback, errorCallback, options);
            }, 5000);

            setWatchId(id);

            return () => {
                clearInterval(locationInterval);
                navigator.geolocation.clearWatch(id);
            };
        }
    };

    // Fetch delivery details
    const fetchDeliveryDetails = useCallback(async () => {
        try {
            console.log("🔄 Fetching delivery details...");

            const response = await axios.get(`${DEV}/delivery/${id}`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (response.status === 200 && response.data.success) {
                const { delivery, stops, route, statistics, currentStop: current } = response.data.data;

                console.log("✅ Delivery data received:", {
                    deliveryId: delivery.deliveryId,
                    stopsCount: stops.length,
                    deliveredStops: stops.filter((s) => s.status === "Delivered").length,
                    statistics: statistics,
                });

                setDeliveryData({ ...delivery, _refreshTime: Date.now() });
                setDeliveryStops([...stops.map((stop) => ({ ...stop }))]);
                setDeliveryRoute(route ? { ...route } : null);
                setDeliveryStatistics(statistics ? { ...statistics, _refreshTime: Date.now() } : null);
                setCurrentStop(current ? { ...current } : null);

                if (delivery.startTime && delivery.status === "In Transit") {
                    const startTime = new Date(delivery.startTime);
                    const now = new Date();
                    const elapsed = Math.floor((now - startTime) / 1000);
                    setElapsedTime(elapsed);
                }

                console.log("✅ All states updated with NEW references");
            } else {
                throw new Error(response.data.message || "Failed to fetch delivery details");
            }
        } catch (err) {
            console.error("❌ Error fetching delivery details:", err);
            setError(err.message);
        }
    }, [id]);

    // Fetch active pause
    const fetchActivePause = async () => {
        try {
            const response = await axios.get(`${DEV}/delivery/${id}/active-pause`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (response.data.success && response.data.data) {
                setActivePause(response.data.data);
                setIsPaused(true);

                const pauseStart = new Date(response.data.data.pauseStartTime);
                const now = new Date();
                const elapsed = Math.floor((now - pauseStart) / 1000);
                setPauseElapsedTime(elapsed);
            }
        } catch (error) {
            console.error("Error fetching active pause:", error);
        }
    };

    // Update stop status
    const updateStopStatus = async (stopId, newStatus, incompleteReason = null) => {
        await fetchDeliveryDetails();

        // Auto-reorder remaining stops after status change
        if (driverLocation && deliveryData?.status === "In Transit") {
            setTimeout(() => {
                reorderDeliveryStops(driverLocation);
            }, 500); // Small delay to ensure UI updates first
        }

        console.log("Stop status updated, stops reordered");
    };

    // Navigation functions
    const startNavigation = async () => {
        setNavigationActive(true);
        if (isPaused) {
            setIsPaused(false);
        }
        getCurrentLocation();
        setShouldCalculateRoute(true);

        console.log("📍 Navigation started");

        if (toast.current) {
            toast.current.show({
                severity: "success",
                summary: "Navigation Started",
                detail: "Turn-by-turn directions activated",
                life: 3000,
            });
        }
    };

    const stopNavigation = () => {
        setNavigationActive(false);
        setShouldCalculateRoute(false);

        if (watchId) {
            navigator.geolocation.clearWatch(watchId);
            setWatchId(null);
        }

        console.log("📍 Navigation stopped");

        if (toast.current) {
            toast.current.show({
                severity: "info",
                summary: "Navigation Stopped",
                detail: "Directions deactivated",
                life: 3000,
            });
        }
    };

    // Handle pause button click
    const handlePause = () => {
        if (viewOnly) {
            if (toast.current) {
                toast.current.show({ severity: "warn", summary: "Clock In Required", detail: "An active shift is required to perform this action. Please clock in.", life: 4000 });
            }
            return;
        }
        setShowPauseDialog(true);
    };

    // Handle pause confirmation
    const handleConfirmPause = async (pauseData) => {
        if (viewOnly) {
            if (toast.current) {
                toast.current.show({ severity: "warn", summary: "Clock In Required", detail: "An active shift is required to perform this action. Please clock in.", life: 4000 });
            }
            return;
        }
        if (pauseData.resume) {
            try {
                const response = await axios.post(
                    `${DEV}/delivery/${id}/resume`,
                    {},
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    },
                );

                if (response.data.success) {
                    setIsPaused(false);
                    setActivePause(null);
                    setPauseElapsedTime(0);

                    if (toast.current) {
                        toast.current.show({
                            severity: "success",
                            summary: "Delivery Resumed",
                            detail: `Paused for ${formatTime(pauseElapsedTime)}`,
                            life: 3000,
                        });
                    }

                    await fetchDeliveryDetails();
                }
            } catch (error) {
                console.error("Error resuming delivery:", error);
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Resume Failed",
                        detail: error.response?.data?.message || "Failed to resume delivery",
                        life: 3000,
                    });
                }
            }
        } else {
            try {
                const response = await axios.post(
                    `${DEV}/delivery/${id}/pause`,
                    {
                        reason: pauseData.reason,
                        estimatedDuration: pauseData.estimatedDuration,
                    },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    },
                );

                if (response.data.success) {
                    setIsPaused(true);
                    setActivePause(response.data.data);
                    setPauseElapsedTime(0);

                    if (toast.current) {
                        toast.current.show({
                            severity: "info",
                            summary: "Delivery Paused",
                            detail: `Reason: ${pauseData.reason}`,
                            life: 3000,
                        });
                    }

                    await fetchDeliveryDetails();
                }
            } catch (error) {
                console.error("Error pausing delivery:", error);
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Pause Failed",
                        detail: error.response?.data?.message || "Failed to pause delivery",
                        life: 3000,
                    });
                }
            }
        }
    };

    // Stop menu handling
    const handleStopMenu = (stop, event) => {
        event.preventDefault();
        event.stopPropagation();

        setSelectedStopForMenu(stop);

        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({
            x: event.clientX || rect.right,
            y: event.clientY || rect.top,
        });

        setShowStopMenu(true);
    };

    const hideStopMenu = () => {
        setShowStopMenu(false);
        setSelectedStopForMenu(null);
    };

    // Complete stop function
    const completeCurrentStop = async (stopId) => {
        console.log("🎯 Completing stop:", stopId);
        await updateStopStatus(stopId, "Delivered");
    };

    // Complete delivery handler
    const handleCompleteDelivery = async () => {
        if (viewOnly) {
            if (toast.current) {
                toast.current.show({ severity: "warn", summary: "Clock In Required", detail: "An active shift is required to perform this action. Please clock in.", life: 4000 });
            }
            return;
        }
        try {
            const response = await axios.post(
                `${DEV}/delivery/${id}/complete`,
                {},
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                },
            );

            if (response.data.success) {
                locationTrackingService.stopTracking();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Delivery Completed",
                        detail: "All stops delivered successfully!",
                        life: 5000,
                    });
                }

                await fetchDeliveryDetails();

                setTimeout(() => {
                    history.push("/deliveries");
                }, 2000);
            } else {
                console.error("Complete delivery failed (success:false):", response.data);
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Completion Failed",
                        detail: response.data.message || "Could not complete delivery. Please try again.",
                        life: 5000,
                    });
                }
            }
        } catch (error) {
            console.error("Error completing delivery:", error);
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Completion Failed",
                    detail: error.response?.data?.message || "Failed to complete delivery",
                    life: 3000,
                });
            }
        }
    };

    // Mark incomplete handler
    const handleMarkDeliveryIncomplete = async () => {
        try {
            const response = await axios.post(
                `${DEV}/delivery/${id}/mark-incomplete`,
                {},
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                },
            );

            if (response.data.success) {
                locationTrackingService.stopTracking();

                if (toast.current) {
                    toast.current.show({
                        severity: "warn",
                        summary: "Delivery Marked Incomplete",
                        detail: "Some stops were not delivered",
                        life: 5000,
                    });
                }

                setTimeout(() => {
                    history.push("/deliveries");
                }, 2000);
            }
        } catch (error) {
            console.error("Error marking delivery incomplete:", error);
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Update Failed",
                    detail: error.response?.data?.message || "Failed to update delivery",
                    life: 3000,
                });
            }
        }
    };

    // Format time helper
    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    // Effects
    useEffect(() => {
        if (id) {
            setLoading(true);
            fetchDeliveryDetails().finally(() => setLoading(false));
            fetchActivePause();
        }
    }, [id, fetchDeliveryDetails]);

    useEffect(() => {
        getCurrentLocation();
        return () => {
            if (watchId) {
                navigator.geolocation.clearWatch(watchId);
            }
        };
    }, []);

    // Auto-optimize route when delivery page loads and delivery is "In Transit"
    useEffect(() => {
        if (!loading && deliveryData?.status === "In Transit" && driverLocation && !autoOptimizedRef.current) {
            autoOptimizedRef.current = true;
            reorderDeliveryStops(driverLocation);
        }
    }, [loading, deliveryData?.status, driverLocation, reorderDeliveryStops]);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth >= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        let interval;
        if (!isPaused && navigationActive && deliveryData?.status === "In Transit") {
            interval = setInterval(() => {
                setElapsedTime((prev) => prev + 1);
            }, 1000);
        }
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [isPaused, navigationActive, deliveryData?.status]);

    useEffect(() => {
        let interval;
        if (isPaused && activePause) {
            interval = setInterval(() => {
                setPauseElapsedTime((prev) => prev + 1);
            }, 1000);
        }
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [isPaused, activePause]);

    useEffect(() => {
        const sidebar = document.querySelector(".layout-sidebar");
        if (sidebar) {
            sidebar.style.display = "none";
        }
        return () => {
            if (sidebar) {
                sidebar.style.display = "";
            }
        };
    }, []);

    // Loading and error states
    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}>
                    <i className="pi pi-spin pi-spinner" />
                </div>
                <p>Loading delivery details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <div className={styles.errorContent}>
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: "3rem", color: "#ef4444", marginBottom: "20px" }} />
                    <h3>Error Loading Delivery</h3>
                    <p>{error}</p>
                    <div className={styles.errorActions}>
                        <button onClick={() => fetchDeliveryDetails()}>Retry</button>
                        <button onClick={() => window.history.back()}>Go Back</button>
                    </div>
                </div>
            </div>
        );
    }

    if (!deliveryData) {
        return (
            <div className={styles.notFoundContainer}>
                <div className={styles.notFoundContent}>
                    <i className="pi pi-search" style={{ fontSize: "3rem", color: "#6b7280", marginBottom: "20px" }} />
                    <h3>Delivery Not Found</h3>
                    <p>The requested delivery could not be found.</p>
                    <button onClick={() => window.history.back()}>Go Back</button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.deliveryTrackingContainer}>
            <Toast ref={toast} />

            <TopNavigationBar
                deliveryData={deliveryData}
                deliveryStatistics={deliveryStatistics}
                currentStop={currentStop}
                elapsedTime={elapsedTime}
                isPaused={isPaused}
                pauseElapsedTime={pauseElapsedTime}
                navigationActive={navigationActive}
                startNavigation={startNavigation}
                stopNavigation={stopNavigation}
                driverLocation={driverLocation}
                formatTime={formatTime}
                handlePause={handlePause}
                deliveryStops={deliveryStops}
                onCompleteDelivery={handleCompleteDelivery}
                onMarkIncomplete={handleMarkDeliveryIncomplete}
                offlineCount={offlineCount}
                isSyncing={isSyncing}
                onManualSync={syncOfflineDeliveries}
                onOpenDirections={() => setShowDirections(true)}
                toast={toast}
                optimizationInProgress={optimizationInProgress}
                lastOptimizedTime={lastOptimizedTime}
                onOptimizeRoute={() => reorderDeliveryStops()}
            />

            {locationError && <LocationBanner locationError={locationError} getCurrentLocation={getCurrentLocation} />}

            <div className={styles.stopListWrapper}>
                <BottomDrawer
                    deliveryStops={deliveryStops}
                    deliveryStatistics={deliveryStatistics}
                    isDesktop={isDesktop}
                    updateStopStatus={updateStopStatus}
                    toast={toast}
                    onStopMenu={handleStopMenu}
                    onCompleteStop={completeCurrentStop}
                    driverLocation={driverLocation}
                    deliveryId={id}
                    onStopUpdated={fetchDeliveryDetails}
                    setDeliveryStops={setDeliveryStops}
                />
            </div>

            <DirectionsModal visible={showDirections} onHide={() => setShowDirections(false)}>
                <DeliveryMap
                    deliveryStops={deliveryStops}
                    driverLocation={driverLocation}
                    shouldCalculateRoute={shouldCalculateRoute}
                    setShouldCalculateRoute={setShouldCalculateRoute}
                    navigationActive={navigationActive}
                    toast={toast}
                    setCurrentStop={setCurrentStop}
                    currentStop={currentStop}
                    embedded={true}
                    startNavigation={startNavigation}
                    stopNavigation={stopNavigation}
                />
            </DirectionsModal>

            <StopMenu visible={showStopMenu} onHide={hideStopMenu} selectedStop={selectedStopForMenu} deliveryStops={deliveryStops} setDeliveryStops={setDeliveryStops} driverLocation={driverLocation} deliveryId={id} position={menuPosition} />

            <StopNavigationDialog visible={showStopDialog} onHide={() => setShowStopDialog(false)} selectedStop={selectedStop} driverLocation={driverLocation} />

            <PauseDialog visible={showPauseDialog} onHide={() => setShowPauseDialog(false)} onConfirmPause={handleConfirmPause} isPaused={isPaused} />
        </div>
    );
};

export default DeliveryTracking;
